import { useHotkey } from '@modules/common/hooks';
import { QueryBuilderActionType } from '@modules/queryBuilder/store/queryBuilderActionType';
import { useQueryBuilderStore } from '@modules/queryBuilder/store/useQueryBuilderStore';
import { QueryOnChangeProps } from '@modules/queryBuilder/types';
import { PlayArrowRounded } from '@mui/icons-material';
import { Button, ButtonBaseActions, keyframes } from '@mui/material';
import { useEffect, useRef } from 'react';

interface QueryBuilderActionsProps {
  onExecute?: (params: QueryOnChangeProps) => void;
  forceEnableExecuteButton: boolean;
  id?: string;
  shouldRenderExecuteButton?: boolean;
}

const wiggleAnimation = keyframes`
  10%, 90% {
    transform: translate3d(-1px, 0, 0);
  }
  20%, 80% {
    transform: translate3d(2px, 0, 0);
  }
  30%, 50%, 70% {
    transform: translate3d(-4px, 0, 0);
  }
  40%, 60% {
    transform: translate3d(4px, 0, 0);
  }
`;

export function QueryBuilderActions({
  onExecute,
  forceEnableExecuteButton,
  id,
  shouldRenderExecuteButton = true,
}: QueryBuilderActionsProps) {
  const buttonActionsRef = useRef<ButtonBaseActions>(null);
  const dispatch = useQueryBuilderStore(({ dispatch }) => dispatch);
  const shouldWiggleCta = useQueryBuilderStore(
    ({ metadata }) => metadata.hasUnsavedChanges && !metadata.isActive,
  );
  const whereClause = useQueryBuilderStore(
    ({ metadata }) => metadata.whereClause,
  );
  const shouldDisableExecuteCTA = useQueryBuilderStore(
    ({ metadata }) => metadata.shouldDisableExecuteCTA,
  );

  const shouldDisableExecuteButton = useQueryBuilderStore(({ metadata }) =>
    forceEnableExecuteButton
      ? Boolean(metadata.error) || shouldDisableExecuteCTA
      : !metadata.hasUnsavedChanges ||
        metadata.hasIncompleteFilters ||
        Boolean(metadata.error) ||
        shouldDisableExecuteCTA,
  );
  const shouldFocusExecuteCTA = useQueryBuilderStore(
    ({ metadata }) => metadata.shouldFocusExecuteCTA,
  );
  const isActive = useQueryBuilderStore(({ metadata }) => metadata.isActive);

  useEffect(() => {
    if (shouldFocusExecuteCTA && !shouldDisableExecuteButton) {
      requestAnimationFrame(() => {
        buttonActionsRef.current?.focusVisible();

        dispatch({
          type: QueryBuilderActionType.SET_METADATA,
          payload: {
            shouldFocusExecuteCTA: false,
          },
        });
      });
    }
  }, [shouldFocusExecuteCTA, shouldDisableExecuteButton, dispatch]);

  const onGo = () => {
    onExecute?.({
      whereClause,
    });

    dispatch({
      type: QueryBuilderActionType.SET_METADATA,
      payload: {
        hasUnsavedChanges: false,
      },
    });
  };

  useHotkey({
    hotkey: '$mod+enter',
    onHotkey: onGo,
    isHotKeyDisabled: shouldDisableExecuteButton || !isActive,
    actionTitle: 'Execute Query Builder',
  });

  if (!shouldRenderExecuteButton) {
    return null;
  }

  return (
    <Button
      id={id}
      variant="contained"
      action={buttonActionsRef}
      startIcon={<PlayArrowRounded />}
      size="small"
      onClick={onGo}
      disabled={shouldDisableExecuteButton}
      sx={{
        animation:
          !shouldDisableExecuteButton && shouldWiggleCta
            ? `${wiggleAnimation} 1s linear infinite`
            : undefined,
      }}
    >
      Go
    </Button>
  );
}
